import { useContext } from "react";
import { langContext, type Language } from "../api/Lang";
import {
    PROFILE_VALIDATION_EMAIL,
    PROFILE_VALIDATION_EMAIL_VERIFIED,
    PROFILE_VALIDATION_FNAME,
    PROFILE_VALIDATION_LNAME,
    PROFILE_VALIDATION_MOBILE,
    PROFILE_VALIDATION_NO_PEOPLE,
    PROFILE_VALIDATION_ROLE,
    PROFILE_VALIDATION_STAFF_JOB_ROLE,
    PROFILE_VALIDATION_STUDENT_CLASSROOM,
    PROFILE_VALIDATION_STUDENT_FNAME,
    PROFILE_VALIDATION_STUDENT_LNAME,
    PROFILE_VALIDATION_SUCCESS,
} from "./validation";

const el = {
    authLoadingPleaseWait: "Παρακαλώ, περιμένετε...",
    profileLoadingPleaseWait: "Παρακαλώ περιμένετε....",
    packageTitleSuffix: " Πακέτο",
    packageRegisteredBody:
        "Το πακέτο γευμάτων σου έχει καταχωρηθεί. Μπορείς να δεις τα προεπιλεγμένα σου γεύματα στις παραγγελίες σου.",
    packageCopyFinishingByUs:
        "Το πακέτο σου καταχωρήθηκε, αλλά κάποια γεύματα δεν έχουν αντιγραφεί ακόμα. Θα τα ολοκληρώσουμε εμείς — δεν χρειάζεται να κάνεις κάτι.",
    packageRegisterFailedTitle: "Η καταχώρηση δεν ολοκληρώθηκε",
    packageGenericTitle: "Πακέτο γευμάτων",
    paymentSuccessTitle: "Επιτυχής Πληρωμή",
    paymentSuccessMsg: "Μπορείς να δεις το ανανεωμένο υπόλοιπο στον λογαριασμό σου",
    paymentFailTitle: "Αποτυχία Πληρωμής",
    paymentFailMsg: "Παρακαλώ δοκίμασε ξανά ή επικοινώνησε μαζί μας",
    profileDlgErrorTitle: "Σφάλμα Προφίλ",
    profileIncompleteNavigatePrompt:
        "Πρέπει να συμπληρώσεις όλα τα στοιχεία σου για να προχωρήσεις σε παραγγελία. Συμπλήρωσέ τα από τον Λογαριασμό σου.",
    schoolGroupErrorTitle: "Σφάλμα Σχολείου",
    schoolGroupErrorMessage:
        "Είστε ήδη συνδεδεμένοι σε άλλο σχολείο με αυτόν τον λογαριασμό. Για να εγγραφείτε σε άλλο σχολείο, παρακαλώ δημιουργήστε νέο λογαριασμό.",
    profileErrorFname: "Συμπλήρωσε σωστά το όνομά σου",
    profileErrorLname: "Συμπλήρωσε σωστά το επώνυμό σου",
    profileErrorEmail: "Συμπλήρωσε σωστά το email σου",
    profileErrorMobile: "Συμπλήρωσε σωστά το κινητό σου τηλέφωνο",
    profileErrorEmailVerified: "Επαλήθευσε το email σου",
    profileErrorRole: "Συμπλήρωσε σωστά την ιδιότητά σου",
    profileErrorNoPeople: "Δεν έχεις δηλώσει μαθητές ή προσωπικό",
    profileErrorStudentFname: "Συμπλήρωσε σωστά το όνομα του μαθητή",
    profileErrorStudentLname: "Συμπλήρωσε σωστά το επώνυμο του μαθητή",
    profileErrorStudentClassroom: "Συμπλήρωσε σωστά την τάξη του μαθητή",
    profileErrorStaffJobRole: "Συμπλήρωσε σωστά τον ρόλο σου στο σχολείο",
    profileErrorUnknown: "Άγνωστο σφάλμα",
};

const en: typeof el = {
    authLoadingPleaseWait: "Please, wait...",
    profileLoadingPleaseWait: "Please wait....",
    packageTitleSuffix: " Package",
    packageRegisteredBody:
        "Your meal package has been registered. You may check your pre-ordered meals in your everyday orders",
    packageCopyFinishingByUs:
        "Your package is registered, but some meals are not copied yet. We will finish this for you — nothing you need to do.",
    packageRegisterFailedTitle: "Registration did not complete",
    packageGenericTitle: "Meal package",
    paymentSuccessTitle: "Successful payment",
    paymentSuccessMsg: "You may see your new balance in your Account page",
    paymentFailTitle: "Unsuccessful payment",
    paymentFailMsg: "Please try again or contact us",
    profileDlgErrorTitle: "Profile Error",
    profileIncompleteNavigatePrompt:
        "You must fill all your details in order to proceed to orders. Complete them in your Account.",
    schoolGroupErrorTitle: "School Error",
    schoolGroupErrorMessage:
        "You are already logged in to another school with this account. To register to another school, please create a new account.",
    profileErrorFname: "Fill in your name correctly",
    profileErrorLname: "Fill in your surname correctly",
    profileErrorEmail: "Fill in your email correctly",
    profileErrorMobile: "Fill in your mobile phone correctly",
    profileErrorEmailVerified: "Please verify your email",
    profileErrorRole: "Fill in your role correctly",
    profileErrorNoPeople: "You have not entered any students or staff members",
    profileErrorStudentFname: "Fill in the student's name correctly",
    profileErrorStudentLname: "Fill in the student's surname correctly",
    profileErrorStudentClassroom: "Fill in the student's classroom correctly",
    profileErrorStaffJobRole: "Fill in your role in school correctly",
    profileErrorUnknown: "Unknown error",
};

const orderAppLangs = { el, en };

export type OrderAppStrings = typeof el;

export function getOrderAppStrings(lang: Language): OrderAppStrings {
    return orderAppLangs[lang];
}

export function useOrderAppStrings(): OrderAppStrings {
    const [lang] = useContext(langContext);
    return orderAppLangs[lang];
}

/** Maps `checkProfile` / validation codes to a user-visible message. Pure — pass strings from `useOrderAppStrings()` or `getOrderAppStrings`. */
export function getProfErrorDlgMsg(profileErrorCode: number, s: OrderAppStrings): string {
    switch (profileErrorCode) {
        case PROFILE_VALIDATION_FNAME:
            return s.profileErrorFname;
        case PROFILE_VALIDATION_LNAME:
            return s.profileErrorLname;
        case PROFILE_VALIDATION_EMAIL:
            return s.profileErrorEmail;
        case PROFILE_VALIDATION_MOBILE:
            return s.profileErrorMobile;
        case PROFILE_VALIDATION_EMAIL_VERIFIED:
            return s.profileErrorEmailVerified;
        case PROFILE_VALIDATION_ROLE:
            return s.profileErrorRole;
        case PROFILE_VALIDATION_NO_PEOPLE:
            return s.profileErrorNoPeople;
        case PROFILE_VALIDATION_STUDENT_FNAME:
            return s.profileErrorStudentFname;
        case PROFILE_VALIDATION_STUDENT_LNAME:
            return s.profileErrorStudentLname;
        case PROFILE_VALIDATION_STUDENT_CLASSROOM:
            return s.profileErrorStudentClassroom;
        case PROFILE_VALIDATION_STAFF_JOB_ROLE:
            return s.profileErrorStaffJobRole;
        case PROFILE_VALIDATION_SUCCESS:
            return "";
    }
    return s.profileErrorUnknown;
}
